import React, { useMemo } from "react";
import styled from "styled-components";
import { theme } from "@src/theme";
import Tooltip from "./Tooltip";
import { TooltipPosition } from "./Tooltip/Tooltip";

export const UIStyledButton = styled.button`
  background-color: ${theme.colors.gray300};
  color: ${theme.colors.yellow};
  border: 1px solid ${theme.colors.yellow};
  width: 90px;
  height: 30px;
  border-radius: 15px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  user-drag: none;
  -webkit-user-drag: none;
  -moz-user-select: none;
`;
const UIStyledButtonTransparent = styled.button<{ disabled?: boolean }>`
  background: transparent;
  user-select: none;
  color: ${theme.colors.yellow};
  width: 30px;
  height: 30px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  user-drag: none;
  -webkit-user-drag: none;
  -moz-user-select: none;
`;

interface UIButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "danger"
    | "outline"
    | "dangerOutline"
    | "primaryOutline"
    | "secondaryOutline"
    | "transparent"
    | "darkOutline";
  tooltipContent?: string;
  tooltipPosition?: TooltipPosition;
}

const variantStyles: {
  [key in NonNullable<UIButtonProps["variant"]>]: React.CSSProperties;
} = {
  primary: {
    backgroundColor: theme.colors.yellow,
    color: theme.colors.darkBg,
    borderColor: theme.colors.yellow,
  },
  secondary: {
    backgroundColor: theme.colors.gray300,
    color: theme.colors.black,
    borderColor: theme.colors.gray300,
  },
  danger: {
    backgroundColor: theme.colors.red,
    color: theme.colors.white,
    borderColor: theme.colors.red,
  },
  outline: {
    backgroundColor: "transparent",
    color: theme.colors.yellow,
    borderColor: theme.colors.yellow,
  },
  dangerOutline: {
    backgroundColor: "transparent",
    color: theme.colors.red,
    borderColor: theme.colors.red,
  },
  primaryOutline: {
    backgroundColor: "transparent",
    color: theme.colors.yellow,
    borderColor: theme.colors.yellow,
  },
  secondaryOutline: {
    backgroundColor: "transparent",
    color: theme.colors.gray,
    borderColor: theme.colors.gray,
  },
  darkOutline: {
    backgroundColor: "transparent",
    color: theme.colors.black,
    borderColor: theme.colors.black,
  },
  transparent: {
    backgroundColor: "transparent",
    color: theme.colors.yellow,
    borderColor: "transparent",
  },
};

export const UIButton: React.FC<UIButtonProps> = ({
  tooltipContent = "",
  tooltipPosition,
  variant = "primary",
  ...props
}) => {
  if (variant === "transparent") {
    return (
      <Tooltip content={tooltipContent} position={tooltipPosition}>
        <UIStyledButtonTransparent {...props}>
          {props.children}
        </UIStyledButtonTransparent>
      </Tooltip>
    );
  }
  const styles = useMemo(() => variantStyles[variant], [variant]);
  return (
    <Tooltip content={tooltipContent} position={tooltipPosition}>
      <UIStyledButton
        {...props}
        style={{
          width: "65px",
          minHeight: "30px",
          maxHeight: "30px",
          borderRadius: "15px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontWeight: "bold",
          ...props.style,
          border: `1px solid ${styles.borderColor}`,
          backgroundColor: styles.backgroundColor,
          color: styles.color,
          cursor: props.disabled ? "not-allowed" : "pointer",
          opacity: props.disabled ? 0.5 : 1,
        }}
      />
    </Tooltip>
  );
};
